import { Turbo } from "@hotwired/turbo-rails"
import "./controllers"
import * as bootstrap from "bootstrap"

window.bootstrap = bootstrap

// "Are you sure?" as a modal in Sityro's layout, never the browser's pop-up
// (06.10.2026). Turbo calls this for every data-turbo-confirm. The button
// takes the label of the button that was clicked ("Delete", "Disconnect" ...)
// and is red when that one is; data-turbo-confirm-title / -button override.
Turbo.config.forms.confirm = (message, form, submitter) => new Promise((resolve) => {
  const modal = document.getElementById("sityro-confirm")
  if (!modal) return resolve(window.confirm(message))

  const source = (name) => submitter?.getAttribute(name) || form?.getAttribute(name)
  const danger = Boolean(submitter?.matches(".btn-danger, .btn-outline-danger, .text-danger, .sityro-danger") || source("data-turbo-confirm-danger"))
  const label = source("data-turbo-confirm-button") || (submitter?.value || submitter?.textContent || "").trim()

  modal.querySelector(".modal-title").textContent = source("data-turbo-confirm-title") || modal.dataset.defaultTitle
  const body = modal.querySelector("[data-confirm-message]")
  body.replaceChildren(...message.split(/\n{2,}/).map((part) => Object.assign(document.createElement("p"), { textContent: part, className: "mb-2" })))
  modal.querySelector("[data-confirm-icon]").className = `bi fs-3 flex-none ${danger ? "bi-exclamation-octagon text-danger" : "bi-question-circle text-primary"}`
  const ok = modal.querySelector("[data-confirm-ok]")
  ok.textContent = label && label.length <= 40 ? label : ok.dataset.defaultLabel
  ok.className = `btn ${danger ? "btn-danger" : "btn-primary"}`

  let answer = false
  const dialog = bootstrap.Modal.getOrCreateInstance(modal)
  const confirm = () => { answer = true; dialog.hide() }
  ok.addEventListener("click", confirm, { once: true })
  modal.addEventListener("hidden.bs.modal", () => { ok.removeEventListener("click", confirm); resolve(answer) }, { once: true })
  modal.addEventListener("shown.bs.modal", () => ok.focus(), { once: true })
  dialog.show()
})

// Times are shown in local time: tell the server which zone this browser is in.
try {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
  if (zone && !document.cookie.split("; ").includes("tz=" + zone)) {
    document.cookie = "tz=" + zone + "; path=/; max-age=31536000; samesite=lax"
  }
} catch (e) { /* no zone, the server falls back to the account's or UTC */ }

// Bulk actions on the site list: the bar shows once at least one site is checked.
function syncBulkBars() {
  document.querySelectorAll("[data-bulk-bar]").forEach((bar) => {
    const checked = document.querySelectorAll(`input[form="${bar.id}"][name="site_ids[]"]:checked`).length
    bar.hidden = checked === 0
  })
}
document.addEventListener("change", (event) => {
  if (event.target.matches("input[type=checkbox]")) syncBulkBars()
})
document.addEventListener("click", (event) => {
  if (event.target.matches("input[type=checkbox]")) setTimeout(syncBulkBars, 0) // "select all" sets the others in its onclick
})
document.addEventListener("turbo:load", syncBulkBars)

// Status bar (04.10.2026): while Sityro is working, ask again every few
// seconds; when it is done, the page reloads with the results (06.10.2026),
// or -- while someone types -- the bar offers them. While nothing runs it
// still asks now and then (07.10.2026): work started elsewhere (a scan from
// the plugin, a scheduled backup) shows up without a reload.
let activityTimer = null
function pollActivities() {
  clearTimeout(activityTimer)
  const bar = document.getElementById("activity-bar")
  if (!bar) return
  const active = bar.dataset.active === "1"
  activityTimer = setTimeout(async () => {
    if (!active && document.visibilityState !== "visible") return pollActivities()
    try {
      const url = new URL(bar.dataset.url, window.location.href)
      if (active) url.searchParams.set("was_active", "1")
      const response = await fetch(url, { headers: { Accept: "text/html" } })
      // Only the bar itself ever replaces the bar (11.10.2026): an answer that was redirected -- signed out, an
      // open duty -- is a whole page, and put in here it nested the layout once more with every round.
      if (response.ok && !response.redirected) {
        const answer = document.createElement("template")
        answer.innerHTML = await response.text()
        const next = answer.content.firstElementChild
        if (next?.id === "activity-bar" && answer.content.childElementCount === 1) bar.replaceWith(next)
      }
      const fresh = document.getElementById("activity-bar")
      // Someone typing or looking at an open window (the file viewer): no reload under them -- a reload left
      // the viewer's spinner over the page (26.10.2026). The next round tries again.
      const typing = document.activeElement?.matches("input, textarea, select") || !!document.querySelector(".modal.show")
      // Done (06.10.2026): the page shows the results by itself -- unless someone is typing.
      if (fresh?.querySelector(".activity-bar-done") && !typing) return Turbo.visit(window.location.href, { action: "replace" })
      // Started elsewhere: the page as it is now, tiles with their spinners.
      if (!active && fresh?.dataset.active === "1" && !typing) return Turbo.visit(window.location.href, { action: "replace" })
    } catch (e) { /* offline for a moment: try again next round */ }
    pollActivities()
    // A file action is on its way (a row says "in progress"): look sooner, the row leaves when it is through.
  }, document.querySelector(".repair-file.is-working") ? 3000 : (active ? 8000 : 20000))
}

// Pages that ask to be refreshed in place (turbo_refreshes_with method: :morph, 09.10.2026): a form
// that comes back to the same page replaces the visit instead of adding one -- for Turbo that is a
// refresh, so only what changed is swapped and the page stays where it was scrolled to.
document.addEventListener("turbo:submit-start", (event) => {
  const form = event.detail.formSubmission.formElement
  const morph = document.querySelector('meta[name="turbo-refresh-method"][content="morph"]')
  if (morph && form.method.toLowerCase() !== "get" && !form.hasAttribute("data-turbo-action")) form.setAttribute("data-turbo-action", "replace")
})
// What someone opened stays open through such a refresh ("Show all", a folder's files).
document.addEventListener("turbo:before-morph-attribute", (event) => {
  if (event.target.tagName === "DETAILS" && event.detail.attributeName === "open") event.preventDefault()
})
document.addEventListener("turbo:load", pollActivities)
// A row named in the address folds open (13.10.2026): "#check:header_csp" on a site's action plan --
// on arriving, and when a link on the page itself names another row.
function openNamedRow() {
  let id = ""
  try { id = decodeURIComponent(window.location.hash.slice(1)) } catch (e) { return }
  const row = id && document.getElementById(id)
  if (row && row.tagName === "DETAILS") { row.open = true; row.scrollIntoView({ block: "start" }) }
}
document.addEventListener("turbo:load", openNamedRow)
window.addEventListener("hashchange", openNamedRow)

// Trust badge page (04.10.2026): choosing a form or a colour updates the
// previews at once; nothing is saved until "Save".
document.addEventListener("input", (event) => {
  const form = event.target.closest("[data-badge-form]")
  if (!form) return
  const colors = new URLSearchParams()
  form.querySelectorAll("[data-badge-color]").forEach((input) => colors.set(`colors[${input.dataset.badgeColor}]`, input.value))
  form.querySelectorAll("img[data-badge-preview]").forEach((img) => {
    const params = new URLSearchParams(colors)
    params.set("style", img.dataset.badgePreview)
    img.src = `${form.dataset.previewUrl}?${params}`
  })
})

// One sign-in for all tabs of a browser (11.10.2026): when someone signs in as another person, or
// out, in one tab, the others still show a page made for the person before. Each page says whom it
// was made for (meta "sityro-user"), the cookie "sityro_user" says who is signed in now; a tab that
// comes into view and finds the two differ goes to the start page of whoever is signed in.
function sessionUser() {
  const match = document.cookie.match(/(?:^|;\s*)sityro_user=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : ""
}
function followSessionUser() {
  const meta = document.querySelector('meta[name="sityro-user"]')
  if (!meta || document.visibilityState !== "visible") return
  if ((meta.content || "") !== sessionUser()) window.location.replace("/")
}
document.addEventListener("visibilitychange", followSessionUser)
window.addEventListener("focus", followSessionUser)
window.addEventListener("pageshow", followSessionUser)

// A page swap with a window still open (26.10.2026): Bootstrap's backdrop and the locked scrolling would stay
// behind, the window itself is kept by data-turbo-permanent. Close it first, so nothing hangs over the next page.
document.addEventListener("turbo:before-render", () => {
  document.querySelectorAll(".modal.show").forEach((modal) => window.bootstrap?.Modal.getInstance(modal)?.hide())
  document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove())
  document.body.classList.remove("modal-open")
  document.body.style.removeProperty("overflow")
  document.body.style.removeProperty("padding-right")
})
